import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material'
import { format, isValid } from 'date-fns'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import api from '../../../api'
import { NextInspectionDto } from '../../../api/types/checklist'
import { scheduleRepeatOptions } from '../../../constants'
import { TOKENS } from '../../../pages/dashboard-v2/components/primitives'
import { IAlphaprocessState } from '../../../store'

// "Geplant": a click on a planned process shows its schedule; start it from here, and admins and
// power users get to the schedule in the template (the editor opens it at #schedule_<id>)

interface Props {
  item: NextInspectionDto | null
  startLink: (x: NextInspectionDto) => string
  onLatest: (scheduleId: number) => void
  onClose: () => void
}

const when = (value: Date | string | null | undefined) => {
  if (!value) return ''
  const date = new Date(value as any)
  return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm') : ''
}

function Row({ label, children }: { label: string, children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '140px minmax(0,1fr)', gap: 2, py: 0.75 }}>
      <Typography sx={{ fontSize: 13.5, color: TOKENS.muted }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: 14 }}>{children || '-'}</Typography>
    </Box>
  )
}

export default function PlannedProcessDialog({
  item, startLink, onLatest, onClose
}: Props) {
  const { t } = useTranslation()
  const user = useSelector((state: IAlphaprocessState) => state.core.user)
  const role = user?.current_role
  const canEditTemplates = role === 'admin' || role === 'power_user' || !!user?.can_edit_checklists
  const [names, setNames] = useState<string[]>([])

  // who the planned process is for (ids in the list, names here)
  useEffect(() => {
    setNames([])
    if (!item || !user) return
    let active = true
    Promise.all([
      item.user_ids.length ? api.users.all() : Promise.resolve([]),
      item.team_ids.length ? api.teams.all(user.corporation_id) : Promise.resolve([])
    ]).then(([users, teams]: any[]) => {
      if (!active) return
      const userList = Array.isArray(users) ? users : users?.data ?? []
      const teamList = Array.isArray(teams) ? teams : teams?.data ?? []
      setNames([
        ...userList.filter((u) => item.user_ids.includes(u.id)).map((u) => u.full_name),
        ...teamList.filter((x) => item.team_ids.includes(x.id)).map((x) => x.name)
      ].filter(Boolean))
    }).catch(() => undefined)
    return () => { active = false }
  }, [item, user])

  if (!item) return null

  const repeat = scheduleRepeatOptions.find((o) => o.value === item.repeat)
  const canStart = !item.autostart && !item.non_working_day_excluded
  const editorLink = `/templates/editor/${item.checklist_id}`
  const notes = [
    item.autostart ? t('overview.startsAutomatically') : null,
    item.rescheduled_due_to_non_working_day ? t('scheduleList.rescheduled') : null,
    item.non_working_day_excluded ? t('scheduleList.skipped') : null
  ].filter(Boolean)

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        {item.title}
        {item.template_title && item.template_title !== item.title && (
          <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>
            {t('scheduleList.template')}: {item.template_title}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Row label={t('scheduleList.repeat')}>{repeat ? t(repeat.translationKey) : ''}</Row>
        <Row label={t('scheduleList.nextStart')}>{when(item.start_date ?? item.due_date)}</Row>
        <Row label={t('scheduleList.due')}>{when(item.due_date)}</Row>
        <Row label={t('scheduleList.assignees')}>{names.join(', ')}</Row>
        {item.project_name && <Row label={t('scheduleList.project')}>{item.project_name}</Row>}
        {notes.length > 0 && <Row label={t('scheduleList.notes')}>{notes.join(', ')}</Row>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => { onClose(); onLatest(item.schedule_id) }} sx={{ textTransform: 'none', mr: 'auto' }}>
          {t('scheduleList.latest')}
        </Button>
        {canEditTemplates && (
          <>
            <Button component={Link} to={editorLink} onClick={onClose} sx={{ textTransform: 'none' }}>
              {t('scheduleList.openTemplate')}
            </Button>
            <Button component={Link} to={`${editorLink}#schedule_${item.schedule_id}`} onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
              {t('scheduleList.editSchedule')}
            </Button>
          </>
        )}
        {canStart && (
          <Button component={Link} to={startLink(item)} onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
            {t('scheduleList.startNow')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
